// The mock's cells-sim-v0 layers (viewport API 0.3, behind `?mockApi=0.3`): a run's arbors as lines
// coloured by a compartment quantity at the current time, its somata as dots, and its synapse sites
// with their activations as flashes. Schematic like the rest of the mock: it runs the app's cells
// wiring end to end (sim, layer, setTime) before the engine draws these layers.

import { CellsSim } from '../data/sims/cells.ts';
import { SimFiles } from '../data/sims/files.ts';
import { locate } from '../data/sims/time.ts';
import type { CellsHeader } from '../data/sims/types.ts';
import type { Colormap } from './api.ts';
import { project, type Mat4 } from './mat.ts';
import { colormapStops, sampleStops, TRACE } from '../ui/scales.ts';
import { tokens } from '../../../../design/system/tokens.ts';

const NONE = 0xffffffff;
/** Colour steps an arbor is drawn in, one path each; one more for vertices with nothing recorded. */
export const BINS = 32;
/** How long a synapse activation stays lit, s. */
export const FLASH_S = 0.003;
const UNRECORDED = tokens.color.text.tertiary.$value;

/** A `layer` command as the mock holds it. */
export interface MockLayer {
  on: boolean;
  quantity?: string;
  colormap?: Colormap;
  range?: [number, number];
}

/** The colour step of value v over [lo, hi]; BINS when there's no value (not recorded there). */
export function binOf(v: number, lo: number, hi: number): number {
  if (!Number.isFinite(v) || !(hi > lo)) return BINS;
  return Math.max(0, Math.min(BINS - 1, Math.floor(((v - lo) / (hi - lo)) * BINS)));
}

/** The activations lit at t, as indices [i0, i1) into ascending times: those in [t − flash, t]. */
export function litRange(times: ArrayLike<number>, t: number, flash = FLASH_S): [number, number] {
  const first = (pred: (x: number) => boolean) => {
    let lo = 0;
    let hi = times.length;
    while (lo < hi) {
      const m = (lo + hi) >> 1;
      if (pred(times[m]!)) hi = m;
      else lo = m + 1;
    }
    return lo;
  };
  return [first((x) => x >= t - flash), first((x) => x > t)];
}

const rgb = (c: [number, number, number]) => `rgb(${c[0]},${c[1]},${c[2]})`;

export class MockCells {
  private shown: { f: number; quantity: string; values: Float32Array } | null = null;
  private loading = false;
  private want: { f: number; quantity: string } | null = null;
  private proj: { vp: Mat4; w: number; h: number; xy: Float32Array; ok: Uint8Array } | null = null;

  private constructor(
    private readonly sim: CellsSim,
    /** Vertices in the loaded pack's space (the run's placement applied), mm. */
    private readonly xyz: Float32Array,
    private readonly segments: Uint32Array,
    private readonly vertexCompartment: Uint32Array,
    /** Each cell's soma: the first vertex its soma compartment colours. */
    private readonly somata: { vertex: number; compartment: number }[],
    private readonly synapses: { vertex: Uint32Array; times: Float32Array; synapse: Uint32Array } | null,
  ) {}

  static async open(assets: string, path: string, header: CellsHeader): Promise<MockCells> {
    const files = new SimFiles({ base: `${assets}sims/`, fetch: (...a) => fetch(...a) }, path);
    const sim = await CellsSim.open(files, header);
    const [pos, segments, vc] = (await Promise.all([files.raw('morph/positions.f32'), files.raw('morph/segments.u32'), files.raw('morph/vertex_compartment.u32')])) as [Float32Array, Uint32Array, Uint32Array];
    const a = header.placement?.affine;
    const xyz = new Float32Array(pos.length);
    for (let v = 0; v < pos.length; v += 3) {
      const x = pos[v]!;
      const y = pos[v + 1]!;
      const z = pos[v + 2]!;
      for (let r = 0; r < 3; r++) xyz[v + r] = a ? a[r]![0]! * x + a[r]![1]! * y + a[r]![2]! * z + a[r]![3]! : pos[v + r]!;
    }
    const somaOf = new Map<number, number>();
    for (const cell of sim.cells) somaOf.set(await sim.somaCompartment(cell), -1);
    for (let v = 0; v < vc.length; v++) if (somaOf.get(vc[v]!) === -1) somaOf.set(vc[v]!, v);
    const somata = [...somaOf].filter(([, v]) => v >= 0).map(([compartment, vertex]) => ({ vertex, compartment }));
    const synapses =
      typeof header.files?.synapses === 'string' && header.files.synapses
        ? await Promise.all([files.raw('synapses/vertex.u32'), files.raw('synapses/act_times_s.f32'), files.raw('synapses/act_synapse.u32')]).then(
            ([vertex, times, synapse]) => ({ vertex: vertex as Uint32Array, times: times as Float32Array, synapse: synapse as Uint32Array }),
            () => null,
          )
        : null;
    return new MockCells(sim, xyz, segments, vc, somata, synapses);
  }

  /** The quantity per compartment at t's nearest frame; the last frame shown while a new one loads. */
  private valuesAt(quantity: string, t: number, redraw: () => void): Float32Array | null {
    const at = locate(this.sim.axis, t);
    const f = at.w < 0.5 ? at.i0 : at.i1;
    if (this.shown?.f !== f || this.shown.quantity !== quantity) this.request({ f, quantity }, redraw);
    return this.shown?.quantity === quantity ? this.shown.values : null;
  }

  /** One frame load at a time: a newer request waits for the one under way, then replaces it. */
  private request(next: { f: number; quantity: string }, redraw: () => void) {
    this.want = next;
    if (this.loading) return;
    this.loading = true;
    this.sim
      .frame(next.quantity, next.f)
      .then(
        (values) => {
          this.shown = { ...next, values };
        },
        (err: unknown) => console.warn('[mock cells]', err),
      )
      .finally(() => {
        this.loading = false;
        const w = this.want;
        if (w && (w.f !== next.f || w.quantity !== next.quantity)) this.request(w, redraw);
        redraw();
      });
  }

  private project(vp: Mat4, w: number, h: number) {
    if (this.proj?.vp !== vp || this.proj.w !== w || this.proj.h !== h) {
      const n = this.xyz.length / 3;
      const xy = new Float32Array(n * 2);
      const ok = new Uint8Array(n);
      for (let v = 0; v < n; v++) {
        const p = project(vp, [this.xyz[3 * v]!, this.xyz[3 * v + 1]!, this.xyz[3 * v + 2]!], w, h);
        xy[2 * v] = p.x;
        xy[2 * v + 1] = p.y;
        ok[v] = p.behind ? 0 : 1;
      }
      this.proj = { vp, w, h, xy, ok };
    }
    return this.proj;
  }

  draw(ctx: CanvasRenderingContext2D, vp: Mat4, w: number, h: number, t: number, layer: (name: string) => MockLayer | undefined, redraw: () => void) {
    const arbors = layer('arbors');
    const somata = layer('somata');
    const synapses = layer('synapses');
    if (!arbors?.on && !somata?.on && !synapses?.on) return;
    const { xy, ok } = this.project(vp, w, h);
    const quantity = arbors?.quantity ?? 'v';
    const values = arbors?.on || somata?.on ? this.valuesAt(quantity, t, redraw) : null;
    const stops = colormapStops(arbors?.colormap ?? 'viridis');
    const [lo, hi] = arbors?.range ?? this.sim.seriesInfo(quantity).range ?? [-80, 40];
    const colour = (bin: number) => (bin >= BINS ? UNRECORDED : rgb(sampleStops(stops, (bin + 0.5) / BINS)));
    const value = (c: number) => (c !== NONE && values ? values[c]! : NaN);
    ctx.save();
    ctx.lineCap = 'round';
    if (arbors?.on) {
      const paths = Array.from({ length: BINS + 1 }, () => new Path2D());
      const seg = this.segments;
      const vc = this.vertexCompartment;
      for (let s = 0; s + 1 < seg.length; s += 2) {
        const a = seg[s]!;
        const b = seg[s + 1]!;
        if (!ok[a] || !ok[b]) continue;
        const path = paths[binOf(value(vc[b] !== NONE ? vc[b]! : vc[a]!), lo, hi)]!;
        path.moveTo(xy[2 * a]!, xy[2 * a + 1]!);
        path.lineTo(xy[2 * b]!, xy[2 * b + 1]!);
      }
      ctx.lineWidth = 1.25;
      paths.forEach((p, bin) => {
        ctx.strokeStyle = colour(bin);
        ctx.stroke(p);
      });
    }
    if (somata?.on)
      for (const s of this.somata) {
        if (!ok[s.vertex]) continue;
        ctx.beginPath();
        ctx.arc(xy[2 * s.vertex]!, xy[2 * s.vertex + 1]!, 4, 0, Math.PI * 2);
        ctx.fillStyle = colour(binOf(value(s.compartment), lo, hi));
        ctx.fill();
        ctx.lineWidth = 1;
        ctx.strokeStyle = tokens.color.text.primary.$value;
        ctx.stroke();
      }
    if (synapses?.on && this.synapses) {
      const { vertex, times, synapse } = this.synapses;
      const dot = (v: number | undefined, r: number) => {
        if (v === undefined || v === NONE || !ok[v]) return;
        ctx.beginPath();
        ctx.arc(xy[2 * v]!, xy[2 * v + 1]!, r, 0, Math.PI * 2);
        ctx.fill();
      };
      ctx.fillStyle = TRACE.event;
      ctx.globalAlpha = 0.3;
      for (let i = 0; i < vertex.length; i++) dot(vertex[i], 1.5);
      const [i0, i1] = litRange(times, t);
      for (let i = i0; i < i1; i++) {
        ctx.globalAlpha = Math.max(0.2, 1 - (t - times[i]!) / FLASH_S);
        dot(vertex[synapse[i]!], 3.5);
      }
    }
    ctx.restore();
  }
}
